import React from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Text } from '@zillow/constellation-10';
import { SpinnerContainer } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors } from '../../../selectors';

import { ZRMOverview } from '../ZRMOverview';
import { NoSearchResults } from '../NoSearchResults';
import PropertiesViewGrid from '../PropertiesViewGrid';
import PropertiesViewList from '../PropertiesViewList';

import type { PropertyProps } from '../../../model/Property';

export type Props = {
  listings: Array<PropertyProps>;
  showArchived: boolean;
  isLoading: boolean;
  didPropertiesLoad: boolean;
  isGetPropertiesError?: boolean;
  onRemoveSearchTextClick?: (value: string) => void;
  hasAnyArchivedListings: boolean;
  toggleArchivedView: () => void;
};

const PropertiesDisplay = ({
  listings,
  showArchived,
  isLoading,
  didPropertiesLoad,
  isGetPropertiesError = false,
  onRemoveSearchTextClick = () => {},
  hasAnyArchivedListings,
  toggleArchivedView,
}: Props) => {
  const { isScreenLgGTE, user, propertiesFilters } = useSelector(
    (state) => ({
      isScreenLgGTE: AppSelectors.makeIsScreenLgGTE(state),
      user: state.user,
      propertiesFilters: state.properties.propertiesFilters,
    }),
    shallowEqual,
  );

  const renderLoadingDisplay = () => {
    return <SpinnerContainer height="300px" />;
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (propertiesFilters.searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={onRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
          You don&apos;t have any archived properties.
        </Text>
      );
    }

    if (user.features.canAddListing) {
      return (
        <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
      );
    }

    return (
      <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
        No properties found.
      </Text>
    );
  };

  const renderView = () =>
    isScreenLgGTE ? <PropertiesViewList listings={listings} /> : <PropertiesViewGrid listings={listings} />;

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listings.length > 0 ? (
        renderView()
      ) : (
        <Card
          outlined={false}
          elevated={false}
          css={{
            marginBlock: '1200',
            backgroundColor: 'transparent',
            textAlign: 'center',
            lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
          }}
        >
          {renderNoListingsFound()}
        </Card>
      )}
    </React.Fragment>
  );

  const renderContent = () => {
    if (!didPropertiesLoad && isLoading) {
      return renderLoadingDisplay();
    }

    if (isGetPropertiesError) {
      return renderTimedOutDisplay();
    }

    return renderListingsDisplay();
  };

  return <Box data-testid="PropertiesDisplay">{renderContent()}</Box>;
};

export { PropertiesDisplay };
export default PropertiesDisplay;
